import {format} from 'date-fns';
import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';

import {DatePicker, Select} from '@/components';
import {Input, NumberInput} from '@/components/common/Input';
import {
  AfriexCountryCode,
  AfriexCurrencyCode,
  AfriexFeatureField,
  AfriexOS,
  AfriexRegionCode,
  BusinessFeeComparator,
} from '@/types';
import {formatSelectOptionItem} from '@/utils/merchant';

type FieldValueType =
  'STRING' | 'NUMBER' | 'DATE' | 'OS' | 'CURRENCY' | 'COUNTRY' | 'REGION';

const mapFieldTypeToValue = (field: AfriexFeatureField): FieldValueType => {
  switch (field) {
    // case AfriexFeatureField.Currency:
    //   return "CURRENCY";
    case AfriexFeatureField.Country:
      return 'COUNTRY';
    case AfriexFeatureField.TransactionVolume:
    case AfriexFeatureField.TransactionCount:
      return 'NUMBER';
    case AfriexFeatureField.OS:
      return 'OS';
    case AfriexFeatureField.SignupDate:
      return 'DATE';
    case AfriexFeatureField.Region:
      return 'REGION';
    default:
      return 'STRING';
  }
};

const valueTypeToOptionsMap: Partial<Record<FieldValueType, string[]>> = {
  CURRENCY: Object.values(AfriexCurrencyCode),
  COUNTRY: Object.values(AfriexCountryCode),
  OS: Object.values(AfriexOS),
  REGION: Object.values(AfriexRegionCode),
};

const listToOptions = (list: string[]) =>
  list.map((action) => ({
    label: action,
    key: formatSelectOptionItem(action),
    value: action,
  }));

type Props = {
  field: AfriexFeatureField;
  comparator: BusinessFeeComparator;
  handleValueChange: (value: string) => void;
};

export const FeatureRuleValueInput = ({
  field,
  comparator,
  handleValueChange,
}: Props): ReactElement => {
  const valueType = mapFieldTypeToValue(field);
  const shouldRenderTwice = comparator === BusinessFeeComparator.Between;
  const options = valueTypeToOptionsMap[valueType];
  const selectOptions = listToOptions(options ?? []);
  const [firstValue, setFirstValue] = useState<string>('');
  const [secondValue, setSecondValue] = useState<string>('');
  // Sends what is on screen. A half-filled range keeps its empty bound's
  // position (`10,`) so it can be explained; an empty one sends nothing, as an
  // empty single value does. Other comparisons send only the first value, so a
  // bound hidden by leaving Between is never sent.
  useEffect(() => {
    handleValueChange(
      shouldRenderTwice && (firstValue || secondValue)
        ? `${firstValue},${secondValue}`
        : firstValue,
    );
  }, [firstValue, secondValue, comparator]);

  useEffect(() => {
    setFirstValue('');
    setSecondValue('');
  }, [valueType]);

  const handleChange = (value: string, isSecond: boolean = false) => {
    if (isSecond) {
      setSecondValue(value);
    } else {
      setFirstValue(value);
    }
  };

  const selectWidget1 = (
    <Select
      id="field"
      name="field"
      options={selectOptions}
      value={firstValue}
      dataCy="merchant-features-form-rule-value"
      className="w-full lg:w-fit"
      placeholder="---Select One---"
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
        handleChange(event.target.value)
      }
    />
  );
  const selectWidget2 = (
    <Select
      id="field"
      name="field"
      options={selectOptions}
      value={secondValue}
      dataCy="merchant-features-form-rule-value-second"
      className="w-full lg:w-fit"
      placeholder="---Select One---"
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
        handleChange(event.target.value, true)
      }
    />
  );

  const numberInputWidget1 = (
    <NumberInput
      min={0}
      value={firstValue}
      dataCy="merchant-features-form-rule-value"
      className="w-fit"
      onValueChange={(rawvalue: string) => handleChange(rawvalue)}
    />
  );
  const numberInputWidget2 = (
    <NumberInput
      min={0}
      value={secondValue}
      dataCy="merchant-features-form-rule-value-second"
      className="w-fit"
      onValueChange={(rawvalue: string) => handleChange(rawvalue, true)}
    />
  );
  // The calendar hands back local midnight on the picked day, so the day is
  // read in local time. Converting to UTC first (toISOString) saved the day
  // before anywhere ahead of UTC, Lagos included.
  const toCalendarDay = (date: Date) => format(date, 'yyyy-MM-dd');

  // react-datepicker does not forward unknown props to its input, so the test
  // hook sits on a wrapper.
  const datePicker1 = (
    <div data-cy="merchant-features-form-rule-value">
      <DatePicker
        value={firstValue}
        setSelectedDate={(date: Date) => {
          handleChange(toCalendarDay(date));
        }}
      />
    </div>
  );
  const datePicker2 = (
    <div data-cy="merchant-features-form-rule-value-second">
      <DatePicker
        value={secondValue}
        setSelectedDate={(date: Date) => {
          handleChange(toCalendarDay(date), true);
        }}
      />
    </div>
  );

  const textInputWidget1 = (
    <Input
      value={firstValue}
      dataCy="merchant-features-form-rule-value"
      className="w-fit"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(e.target.value)
      }
    />
  );
  const textInputWidget2 = (
    <Input
      value={secondValue}
      dataCy="merchant-features-form-rule-value-second"
      className="w-fit"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(e.target.value, true)
      }
    />
  );

  switch (valueType) {
    case 'REGION':
    case 'CURRENCY':
    case 'COUNTRY':
    case 'OS':
      return shouldRenderTwice ? (
        <>
          {selectWidget1}
          {selectWidget2}
        </>
      ) : (
        selectWidget1
      );
    case 'NUMBER':
      return shouldRenderTwice ? (
        <>
          {numberInputWidget1}
          {numberInputWidget2}
        </>
      ) : (
        numberInputWidget1
      );
    case 'DATE':
      return shouldRenderTwice ? (
        <>
          {datePicker1}
          {datePicker2}
        </>
      ) : (
        datePicker1
      );
    default:
      return shouldRenderTwice ? (
        <>
          {textInputWidget1}
          {textInputWidget2}
        </>
      ) : (
        textInputWidget1
      );
  }
};
